import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  CLUSTER_QUERY_KEY,
  VIEW_CLUSTER_QUERY_KEY,
  clusterService,
} from "../../../../../Services/clusterService";
import {
  REGION_QUERY_KEY,
  VIEW_REGION_QUERY_KEY,
  regionService,
} from "../../../../../Services/regionsService";
import { useState } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";
import { Region } from "../../../../../Types/MasterData/Region";
import {
  Cluster,
  ViewCluster,
} from "../../../../../Types/CompanyProfile/Cluster";
import {
  deploymentsiteService,
  VIEW_DEPSITE_QUERY_KEY,
} from "../../../../../Services/deploymentsiteService";

const INITIAL_STATE = {
  name: "",
  status: -1,
  cluster: -1,
};

interface CreateFormProps {
  toggle?: () => void;
  company_code: string; // required
}

const CreateDeploymentSiteForm: React.FC<CreateFormProps> = ({
  company_code,
  toggle,
}) => {
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user);
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);

  const createMutation = useMutation({
    mutationFn: deploymentsiteService.createDeploymentSite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VIEW_DEPSITE_QUERY_KEY });
      toast.success("Deployment Site has been created successfully.");

      setFormData(INITIAL_STATE);

      if (toggle) {
        toggle();
      }
    },
    onError: (error: any) => {
      const responseData = error.response?.data;

      if (responseData?.errors) {
        const firstErrorKey = Object.keys(responseData.errors)[0];
        const errorMessage = responseData.errors[firstErrorKey][0];
        toast.error(errorMessage);
      } else if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error(
          "Unable to create Deployment Site. Please verify the entered information.",
        );
      }
    },
  });

  const { data: regionData, isLoading: isLoading } = useQuery<{
    data: Region[];
  }>({
    queryKey: REGION_QUERY_KEY,
    queryFn: regionService.getAllRegions,
  });

  const regions: Region[] =
    regionData?.data?.filter((item) => item.status === 1) ?? [];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const {
    data: clusterData = [],
    isLoading: ClusterisLoading,
    isError: ClusterisError,
  } = useQuery<ViewCluster[]>({
    queryKey: [VIEW_CLUSTER_QUERY_KEY, selectedRegion],
    queryFn: () =>
      clusterService.getAllViewClusterByCompanyAndRegion(
        company_code as string,
        selectedRegion as number,
      ),
    enabled: !!selectedRegion,
  });
  const clusters = clusterData.filter((item) => Number(item.status) === 1);

  const handleClusterSelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setSelectedRegion(Number(e.target.value));
  };
  const handleSelectedStatus = (status: number) => {
    setFormData((prev) => ({ ...prev, status: status }));
  };

  const handleSelectedCluster = (cluster: number) => {
    setFormData((prev) => ({ ...prev, cluster: cluster }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.status === -1) {
      toast.warn("Please select a Status");
      return;
    }

    if (!user) {
      toast.error("User not authenticated");
      return;
    }

    const payload = {
      ...formData,
    };
    createMutation.mutate(payload);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <Col md="12">
            <Label>
              Region<b className="text-danger">*</b>
            </Label>
            <Input
              id="region"
              name="region"
              type="select"
              required
              disabled={isLoading}
              onChange={(e) => {
                handleClusterSelect(e);
              }}
            >
              <option value="">
                {isLoading ? "Loading..." : "Select Region"}
              </option>
              {!isLoading &&
                regions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </Input>
          </Col>
          <Col md="12">
            <Label>
              Cluster<b className="text-danger">*</b>
            </Label>
            <Input
              id="cluster"
              name="cluster"
              type="select"
              required
              disabled={ClusterisLoading || clusters.length === 0}
              onChange={(e) => handleSelectedCluster(Number(e.target.value))}
            >
              <option value="">Select Cluster</option>

              {clusters.map((cluster) => (
                <option key={cluster.id} value={cluster.id}>
                  {cluster.name}
                </option>
              ))}
            </Input>
          </Col>
          <Col md="12">
            <Label>
              Name<b className="text-danger">*</b>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter New Deployment Site"
              maxLength={150}
              minLength={3}
              required
              value={formData.name}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="12">
            <Label>
              Status<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.status}
              id="status"
              name="status"
              type="select"
              onChange={(e) => handleSelectedStatus(Number(e.target.value))}
            >
              <option disabled value={-1}>
                Select Status
              </option>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </Input>
          </Col>

          <Col xs="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending
                ? "Creating Deployment Site..."
                : "Create"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default CreateDeploymentSiteForm;
